import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import LordIconLazy from '../components/LordIconLazy/LordIconLazy';
import styles from './ForthScreen.module.css';

import money from "../icons/wired-outline-298-coins-loop-spin (1).json";
import clock from "../icons/wired-outline-1334-order-history-hover-pinch.json";
import budjet from "../icons/wired-outline-987-accounting-hover-pinch.json";
import house from "../icons/wired-outline-63-home-hover-3d-roll (3).json";
import connections from "../icons/wired-outline-1007-organization-hover-pinch.json";
import mistakes from "../icons/wired-outline-1140-error-hover-enlarge (1).json";

gsap.registerPlugin(ScrollTrigger);

const LordIcon = ({ data }) => (
  <LordIconLazy data={data} colorize="#ffffff" />
);

const advantages = [
  {
    number: '01',
    title: 'חוסכים עשרות אלפי שקלים',
    description: 'שיפוץ בלי ליווי מקצועי? הספקים ישמחו. כי בלי מישהי שמנהלת משא ומתן, משווה הצעות מחיר ושמה לב לכל שורה בחשבונית - אתם פשוט משלמים יותר. הלקוחות שלי חוסכים בממוצע 15%-30% מהעלות, כי כל שקל עובר דרכי לפני שהוא יוצא.',
    iconData: money,
  },
  {
    number: '02',
    title: 'מונעים טעויות לפני שהן קורות',
    description: 'שקע שלא במקום? ארון שלא נפתח עד הסוף? נקודת חשמל שנשכחה? טעויות כאלה נשמעות קטנות - אבל כל אחת עולה אלפי שקלים ושבועות של עיכוב. כשיש לכם מישהי שבודקת כל פרט מראש, חושבת על כל תרחיש ומתאמת בין כל בעלי המקצוע - הדברים פשוט יושבים נכון מההתחלה.',
    iconData: mistakes,
  },
  {
    number: '03',
    title: 'הפרויקט נגמר בזמן',
    description: 'כולם שומעים את הסיפורים - ״עוד שבועיים״ שהופכים לחודשיים, ספקים שלא מגיעים, עבודות שנתקעות באמצע. בתור מנהלת פרויקטים, אני דואגת שכל דבר קורה בדיוק מתי שהוא צריך לקרות - מתאמת בעלי מקצוע, עוקבת אחרי התקדמות העבודה, ומוודאת שאף אחד לא מאחר. ככה הפרויקט שלכם באמת נגמר בזמן.',
    iconData: clock,
  },
  {
    number: '04',
    title: 'אפס הפתעות בתקציב',
    description: 'אין דבר כזה ״נגלה בדרך״. עוד לפני שמתחילים, אני יושבת איתכם על כל סעיף - חומרים, עבודה, תוספות אפשריות. אתם יודעים מראש בדיוק כמה זה עולה, ומה קורה בכל תרחיש. ככה אין הפתעות, ואין ויכוחים.',
    iconData: budjet,
  },
  {
    number: '05',
    title: 'יודעים איך הכל ייראה - עוד לפני שמתחילים',
    description: 'דמיינו שאתם נכנסים לבית שלכם - עוד לפני שהקבלן התחיל. רואים את הצבעים, את החומרים, את האור שנכנס מהחלון. בדיוק בשביל זה אני בונה הדמיה תלת-ממדית מלאה. אם משהו לא מרגיש נכון - משנים על המסך, לא על הקיר.',
    iconData: house,
  },
  {
    number: '06',
    title: 'ראש שקט מהרגע הראשון',
    description: 'לתאם ספקים, לרדוף אחרי קבלנים, להתעסק ברישוי ובירוקרטיה - זה לא מה שאתם צריכים לעשות. בשביל זה אני כאן. כל הניהול, כל התיאומים, כל כאבי הראש - עליי. אתם פשוט ממשיכים עם החיים, ומגיעים לבית מוכן.',
    iconData: connections,
  },
];

const AdvantagesSection = () => {
  const sectionRef = useRef(null);
  const headerRef = useRef(null);
  const itemRefs = useRef([]);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const ctx = gsap.context(() => {
      // Header entrance
      if (headerRef.current) {
        gsap.fromTo(headerRef.current,
          { opacity: 0, y: 40 },
          {
            opacity: 1, y: 0,
            duration: 1,
            ease: 'power3.out',
            scrollTrigger: {
              trigger: headerRef.current,
              start: 'top 85%',
              toggleActions: 'play none none none',
            },
          }
        );
      }

      // Items entrance with staggered children
      itemRefs.current.filter(Boolean).forEach((item, i) => {
        const isReversed = i % 2 === 0;
        const icon = item.querySelector('[data-icon]');
        const title = item.querySelector('[data-title]');
        const desc = item.querySelector('[data-desc]');

        if (icon) {
          gsap.fromTo(icon,
            { opacity: 0, x: isReversed ? -60 : 60, scale: 0.8 },
            {
              opacity: 1, x: 0, scale: 1,
              duration: 0.9,
              ease: 'power3.out',
              scrollTrigger: {
                trigger: item,
                start: 'top 82%',
                toggleActions: 'play none none none',
              },
            }
          );
        }

        if (title) {
          gsap.fromTo(title,
            { opacity: 0, y: 30 },
            {
              opacity: 1, y: 0,
              duration: 0.8,
              delay: 0.15,
              ease: 'power3.out',
              scrollTrigger: {
                trigger: item,
                start: 'top 82%',
                toggleActions: 'play none none none',
              },
            }
          );
        }

        if (desc) {
          gsap.fromTo(desc,
            { opacity: 0, y: 25 },
            {
              opacity: 1, y: 0,
              duration: 0.8,
              delay: 0.3,
              ease: 'power3.out',
              scrollTrigger: {
                trigger: item,
                start: 'top 82%',
                toggleActions: 'play none none none',
              },
            }
          );
        }
      });
    }, section);

    return () => ctx.revert();
  }, []);

  return (
    <section className={styles.section} ref={sectionRef}>
      <div className={styles.grain} />

      {/* Header */}
      <div className={styles.header} ref={headerRef}>
        {/* eyebrow removed */}
        <h2 className={styles.title}>הכל נראה אחרת</h2>
      </div>

      {/* Items */}
      <div className={styles.items}>
        {advantages.map((adv, i) => (
          <div
            key={i}
            ref={el => itemRefs.current[i] = el}
            className={`${styles.item} ${i % 2 === 0 ? styles.itemReversed : ''}`}
          >
            {/* Icon side */}
            <div className={styles.iconSide} data-icon>
              <div className={styles.iconWrap}>
                <LordIcon data={adv.iconData} />
              </div>
            </div>

            {/* Text side */}
            <div className={styles.textSide}>
              <h3 className={styles.itemTitle} data-title>{adv.title}</h3>
              <p className={styles.itemDesc} data-desc>{adv.description}</p>
            </div>
          </div>
        ))}
      </div>

      {/* CTA */}
      <div className={styles.ctaWrapper}>
        <a href="https://wa.me/972502177062" className={styles.ctaButton}>
          <span className={styles.ctaText}>מורן, בואי נדבר!</span>
          <span className={styles.ctaShine} />
        </a>
      </div>
    </section>
  );
};

export default AdvantagesSection;
